import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, Copy } from "lucide-react";
import { formatIDR } from "../lib/translations";

const EMPTY = { code: "", kind: "percent", value: 20, scope: "both", min_purchase: 0, max_discount: 0, usage_limit: 0, valid_until: "", active: true, description: "" };

export default function AdminVouchers() {
  const { user, loading } = useAuth();
  const [list, setList] = useState([]); const [form, setForm] = useState(EMPTY); const [saving, setSaving] = useState(false);

  const load = () => api.get("/vouchers").then(r => setList(r.data)).catch(()=>{});
  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-teal border-t-transparent rounded-full animate-spin"/></div>;
  if (!user) return <Navigate to="/masuk" replace/>;
  if (!user.is_admin) return <div className="max-w-md mx-auto py-16 text-center text-muted-foreground">Halaman ini hanya untuk admin.</div>;

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null };
      await api.post("/vouchers", payload); toast.success(`Voucher ${form.code} dibuat`);
      setForm(EMPTY); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal buat voucher"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus voucher?")) return;
    try { await api.delete(`/vouchers/${id}`); toast.success("Terhapus"); load(); }
    catch { toast.error("Gagal"); }
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`${code} disalin`); };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="admin-vouchers">
      <div className="flex items-center gap-2 mb-6">
        <Ticket className="w-6 h-6 text-teal"/>
        <h1 className="font-display text-3xl md:text-4xl font-medium">Voucher Admin</h1>
        <span className="text-[10px] tracking-widest uppercase text-teal font-bold bg-teal-soft border border-teal/20 rounded px-2 py-0.5 ml-2">Admin Only</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl p-5 lg:col-span-1" data-testid="voucher-form">
          <h2 className="font-display text-lg mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-teal"/> Buat Voucher</h2>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Kode (auto upper)"><Input required value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} placeholder="LAUNCH20" data-testid="voucher-code"/></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Jenis">
                <select value={form.kind} onChange={(e)=>setForm({...form, kind: e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white">
                  <option value="percent">Persentase %</option><option value="fixed">Fixed IDR</option>
                </select>
              </Field>
              <Field label={form.kind === "percent" ? "Nilai %" : "Nilai IDR"}>
                <Input type="number" value={form.value} onChange={(e)=>setForm({...form, value: +e.target.value})} data-testid="voucher-value"/>
              </Field>
            </div>
            <Field label="Berlaku Untuk">
              <select value={form.scope} onChange={(e)=>setForm({...form, scope: e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white">
                <option value="both">Subscription + Order</option><option value="subscription">Subscription NUNA</option><option value="order">Order Produk</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Min. Belanja"><Input type="number" value={form.min_purchase} onChange={(e)=>setForm({...form, min_purchase: +e.target.value})}/></Field>
              <Field label="Max Diskon (0=∞)"><Input type="number" value={form.max_discount} onChange={(e)=>setForm({...form, max_discount: +e.target.value})}/></Field>
              <Field label="Batas Pakai (0=∞)"><Input type="number" value={form.usage_limit} onChange={(e)=>setForm({...form, usage_limit: +e.target.value})}/></Field>
              <Field label="Berlaku s/d"><Input type="date" value={form.valid_until} onChange={(e)=>setForm({...form, valid_until: e.target.value})}/></Field>
            </div>
            <Field label="Deskripsi"><Input value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} placeholder="Kampanye peluncuran"/></Field>
            <Button type="submit" disabled={saving} className="w-full rounded-md bg-teal hover:bg-teal/90 text-white" data-testid="create-voucher-btn">{saving?"Menyimpan...":"Buat Voucher"}</Button>
          </form>
        </Card>

        <Card className="rounded-2xl p-5 lg:col-span-2" data-testid="voucher-list">
          <h2 className="font-display text-lg mb-4">Daftar Voucher ({list.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-teal/15 text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="text-left py-2">Kode</th><th className="text-left py-2">Diskon</th><th className="text-left py-2">Scope</th><th className="text-right py-2">Terpakai</th><th className="text-left py-2">Berlaku</th><th></th>
                </tr>
              </thead>
              <tbody>
                {list.length===0 ? <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Belum ada voucher.</td></tr> :
                  list.map(v => (
                    <tr key={v.id} className="border-b border-teal/5" data-testid={`voucher-row-${v.id}`}>
                      <td className="py-2 font-mono font-semibold flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded ${v.active?"bg-teal-soft text-teal":"bg-muted text-muted-foreground line-through"}`}>{v.code}</span>
                        <button onClick={()=>copy(v.code)} className="text-muted-foreground hover:text-teal"><Copy className="w-3.5 h-3.5"/></button>
                      </td>
                      <td className="py-2">{v.kind === "percent" ? `${v.value}%` : formatIDR(v.value)}</td>
                      <td className="py-2 text-xs">{v.scope}</td>
                      <td className="py-2 text-right">{v.used_count}{v.usage_limit ? ` / ${v.usage_limit}` : ""}</td>
                      <td className="py-2 text-xs">{v.valid_until ? new Date(v.valid_until).toLocaleDateString("id-ID") : "∞"}</td>
                      <td className="py-2 text-right"><button onClick={()=>del(v.id)} className="text-destructive"><Trash2 className="w-4 h-4"/></button></td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>; }
